import React, { useState, useEffect, useRef } from 'react';

/**
 * StreamingText - Typewriter-style text reveal effect
 *
 * @param {string} text - The full text to stream
 * @param {number} speed - Milliseconds per character (default: 20)
 * @param {function} onComplete - Callback when streaming finishes
 * @param {boolean} showCursor - Whether to show blinking cursor (default: true)
 */
const StreamingText = ({ text, speed = 20, onComplete, showCursor = true }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);
  const hasStartedRef = useRef(false);

  // Keep onComplete ref updated without triggering effect
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!text) {
      setIsComplete(true);
      onCompleteRef.current?.();
      return;
    }

    // Prevent re-running if we've already started streaming this text
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    // Reset state for new text
    setDisplayedText('');
    setIsComplete(false);

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex < text.length) {
        setDisplayedText(text.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(interval);
        setIsComplete(true);
        onCompleteRef.current?.();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <span className="streaming-text">
      {displayedText}
      {showCursor && !isComplete && <span className="streaming-text__cursor">|</span>}
    </span>
  );
};

export default StreamingText;
